"use client";

import { useLingui } from "@lingui/react/macro";
import { clearSignupPrefill, readSignupPrefill } from "@offstreetparking/portal-flow/lib/signup-prefill";
import { useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useParker } from "~/components/providers/AuthProvider";
import { handleErrorOnClient } from "~/lib/auth";
import { routes } from "~/routes";
import { sanitizeRedirectTo } from "~/utils/sanitize-redirect-to";
import { getInvitationDetails } from "../signup/actions";
import { loginWithCompanySSO, loginWithPassword } from "./actions";
import { LoginForm } from "./components/LoginForm";

export default function LoginPage() {
	const { i18n } = useLingui();
	const router = useRouter();
	const searchParams = useSearchParams();
	const redirectToParam = sanitizeRedirectTo(searchParams.get("redirectTo"));
	const redirectTo = redirectToParam || routes.permits();
	const inviteToken = searchParams.get("inviteToken") ?? undefined;
	const prefill = readSignupPrefill();
	const { authState, refreshAuth } = useParker({
		ensureSignedIn: false,
		onAuthorized: () => router.replace(redirectTo),
	});
	const { data: invite, isLoading: isInviteLoading } = useQuery({
		queryKey: ["customer-invite", inviteToken],
		queryFn: () => (inviteToken ? getInvitationDetails({ inviteToken }) : Promise.resolve(null)),
		enabled: !!inviteToken,
		retry: false,
	});

	if (authState !== "unauthenticated" || (!!inviteToken && isInviteLoading)) {
		return null;
	}

	return (
		<CommonLayout variant="auth">
			<LoginForm
				redirectTo={redirectToParam}
				email={invite?.email ?? prefill.email}
				onRedirectToSSO={(values) => loginWithCompanySSO({ ...values, redirectTo })}
				onLoginWithPassword={async (values) => {
					await handleErrorOnClient(
						loginWithPassword({
							...values,
							redirectTo,
							inviteToken,
							permitPublicId: prefill.connectPermitPublicIdentifier,
							locale: i18n.locale,
						}),
					);
					clearSignupPrefill();
					await refreshAuth();
					router.replace(redirectTo);
				}}
			/>
		</CommonLayout>
	);
}
